/**
 * The buyable-product criteria — B1..B5, and what protects each one.
 *
 * ── Why this module exists ──
 *
 * `VERIFICATION-BUYABLE-PRODUCT-PRD.md` §3 defines "buyable" as five things a
 * stranger can do, "and each is protected by a registered gate". That sentence
 * is only true if something checks it. Left in prose, a criterion drifts from
 * its gate the moment either changes — and the drift is invisible, because a
 * criterion that names a gate which no longer exists still *reads* as protected.
 *
 * So the criteria live here as data, each naming the gates that protect it and
 * the units that implement it, and `test-verification-buyable-criteria` asserts
 * that **every gate named here is registered in `scripts/verify-checks.mjs`
 * CHECKS**. That is the anti-vacuity rail for the whole run: a criterion may not
 * claim protection from a gate that does not exist.
 *
 * ── What this module deliberately does NOT do ──
 *
 * It does not decide whether a criterion is met. `evaluateBuyableCriteria` takes
 * gate results as an argument, because a module that could report its own
 * criteria as met would be the product grading its own homework — the exact
 * shape Rule 1 refuses, applied to the release decision. The honest answer to
 * "is B2 met?" comes from running the gates, and this module only says which
 * ones to run and what their passing would and would not establish.
 *
 * The `demonstration` field is the load-bearing honesty here. A green gate over
 * a pure module proves the logic; it does not prove a stranger can do the thing.
 * Each criterion states what still has to be demonstrated by hand, so nobody
 * reads five green gates as five buys.
 */
/* ── The criteria ────────────────────────────────────────────────────────── */
export const BUYABLE_CRITERIA = [
    {
        id: "B1",
        statement: "Paste a workflow description into the product and reach an admitted workflow with derived predicates and a named oracle — or an actionable needs-oracle refusal — in under 30 minutes.",
        personas: ["engineer"],
        gates: ["test-verification-workflow-intake"],
        units: ["U1", "U17"],
        demonstration: "A person who has never seen the repo pastes their own workflow document into /styleguide/verification/workflows/new and reaches a verdict. The gate proves the view-model; only a stranger at a keyboard proves the thirty minutes.",
        caveats: [
            "Compilation is fail-closed on the in-boundary local model. A first-time user without it hits a refusal as their first experience — by design, but it is a real onboarding wall, and the PRD flags the path to satisfying it as an open decision.",
        ],
    },
    {
        id: "B2",
        statement: "Call a served, token-scoped API from their own service and get a run id, verdict, and receipt; receive the verdict by signed webhook.",
        personas: ["engineer"],
        gates: [
            "test-verification-api-server",
            "test-verification-api-executor",
            "test-verification-api-routes",
            "test-verification-api-mount",
            "test-verification-api-tokens",
            "test-verification-webhooks",
            "test-verification-api-receipt-ed25519",
            "test-verification-api-typed-predicates",
        ],
        units: ["U2", "U3", "U4", "U16"],
        demonstration: "A scratch external script with no repo imports beyond fetch drives a locally-spawned host and gets back a run id, a computed verdict, and a receipt from ENGINE handlers. `test-verification-api-mount` records that product-composition demonstration.",
        caveats: [
            "The engine-backed API computes a deterministic evidence-integrity verdict: cited evidence must exist, remain active, and retain a matching content hash. A claim carrying ONLY the legacy opaque `predicate` string still resolves to UNKNOWN, never PASS — nothing can read free text, and an evaluator that guessed at it and signed PASS would be the fabricated verdict this product exists to prevent.",
            "A claim may now also carry a TYPED predicate beside that string, and a typed predicate IS read. It is compiled by the same spec compiler the workflow tier uses, refused at intake (HTTP 400, with its reason) when it is vacuous, unfalsifiable or tautologically falsified, and otherwise bound to an obligation whose deciding evaluator is pinned by ref, version and digest — so which evaluator decided a claim is part of the contract, not a runtime choice. The decision itself is made by the SAME shared deterministic evaluator the canonical protocol tier uses, and aggregated by the same law. `test-verification-api-typed-predicates` proves a typed claim reaching PASS and FAIL, an unfalsifiable one refused with no receipt, and an undecidable one still answering UNKNOWN.",
            "What a typed-predicate PASS from THIS tier means, exactly: this tier registers content ADDRESSES, not content — `verification_api_evidence` holds a hash, a source kind and a source, and no bytes. So exactly ONE predicate kind is decidable here, `content_digest_equals`, and a PASS says the cited evidence is registered to this tenant and its registered digest matches the digest the claim pinned. The bytes were not re-derived (`bytesVerified` is false on every artifact this tier admits) and the evaluator ran in the same process that admitted the evidence, so this is not an independent-execution claim. The four content-reading kinds (`text_contains`, `json_equals`, `exit_code`, `resource_bounds`) are deliberately never bound here: binding one would make the evaluator report FAIL from content nothing read, and a fabricated FAIL is the same defect as a fabricated PASS. `artifact_present` is declined for a different reason — its only route to FAIL is that no evidence of the required kind was cited, and reporting absence as falsehood converts 'we do not know' into 'it is false' (D2 §5.2; the obligation itself is owned by lane L11). Semantic correctness of a subject's behaviour still reads UNKNOWN and says so.",
            "The served API runs on the customer's own machine or VPC — npx plus localhost under the source-available licence. It is not a hosted endpoint we operate, and the documentation must not imply one.",
            "Webhook signatures are HMAC over a shared secret. They prove origin to whoever holds the secret, not to an arbitrary third party.",
            "Receipts minted by THIS API are signed Ed25519 over the tenant's own keypair — the same keystore the canonical protocol receipts use, one active key per tenant, private half never leaving the vault. Its public half is served as trust material at GET /api/verification/universal/trust-root, so a reader who holds that PEM can check a receipt's signature and re-derive its payload hash with any standard Ed25519 implementation, on a machine with no Excellent install. `test-verification-api-receipt-ed25519` proves that end to end in a child process outside this repository that resolves no @excellent module.",
            "What an API-tier receipt still does NOT deliver against B3: it is a bare envelope, not an anchored pack. `binding` is null — no work/attempt/contract/determination lineage — and there is no chain anchor or ledger, so offline verification establishes the receipt's origin and the integrity of its own payload, and says nothing about freshness or ledger position. B3 additionally requires the key FINGERPRINT to be published outside the pack, on the customer's own domain; the product serves the key, but publishing it somewhere a reader trusts independently is an operator act no gate performs. Cite an API-tier receipt for origin and integrity, not as a completed B3 demonstration.",
            "Receipts issued before this cutover are HMAC-SHA256 under a deployment-local secret (key ids prefixed `vek_`). They were not reissued and remain verifiable — the verifier dispatches on each envelope's own algorithm — but only by the holder of that secret, never by a third party. Do not cite one for offline verification.",
        ],
    },
    {
        id: "B3",
        statement: "Verify a pack with a verifier obtained out-of-band, against key fingerprints published outside the pack, on a machine that never ran Excellent.",
        personas: ["ciso", "auditor"],
        gates: [
            "test-verification-trust-root",
            "test-verification-chain-anchor",
            "test-verification-pack-adversarial",
        ],
        units: ["U5", "U6", "U7"],
        demonstration: "The verifier is fetched from a distribution channel independent of the pack and run on a machine with no Excellent install, against fingerprints read from the customer's own domain.",
        caveats: [
            "Signing is HMAC-SHA256, which is symmetric: the out-of-band fingerprint is over a shared secret, so anyone holding it — including us — can mint a pack that anchors perfectly. This is a hard ceiling on 'verifiable without trusting you or us', and clearing it requires asymmetric signing.",
            "Ledger ROLLBACK is not detectable from inside a pack. A genuine anchor replayed against a rolled-back ledger passes every hash check, because it did when it was honest. Detection requires the reader to hold an out-of-band expectation of the current anchor; an anchor cannot prove its own freshness.",
            "Replay proves internal consistency, not collection fidelity. Whether an export faithfully represents the source system is still trust in the collection process, and an auditor should still test collection.",
        ],
    },
    {
        id: "B4",
        statement: "Answer the enterprise security questionnaire from the product's own posture pages — custody, scopes, egress, anchoring — without a call.",
        personas: ["ciso"],
        gates: ["test-verification-posture-statement"],
        units: ["U13", "U17"],
        demonstration: "A security reviewer completes their questionnaire from /styleguide/verification/posture alone. Unanswerable rows must be visible as unanswered rather than absent.",
        caveats: [
            "'Who verifies the verifier' is not answered: there is no third-party pentest or audit of Excellent itself, and the scorecard is self-measurement. This is stated as not-yet rather than omitted.",
        ],
    },
    {
        id: "B5",
        statement: "Work a 200-finding week with one reviewer, without silent approvals — the silent-approve rate stays hard-zero.",
        personas: ["reviewer", "compliance-officer"],
        gates: [
            "test-verification-review-clustering",
            "test-verification-review-bulk",
            "test-verification-rationale-drafts",
        ],
        units: ["U8", "U9", "U10", "U17"],
        demonstration: "A seeded 200-finding week is worked end to end in the fixture by one reviewer, with silent_approve_rate measured at zero rather than assumed.",
        caveats: [
            "Drafted rationales reduce cost per decision and create a new risk the product must be able to see: a reviewer who signs every draft unedited. drafted_rationale_edit_rate exists so rubber-stamping is visible in aggregate; a low edit rate is a signal, not a pass.",
        ],
    },
];
export const GAUNTLET_BLOCKERS = [
    {
        id: "F1",
        summary: "Flip the lead: workflow/agent verification first, SOC 2 as one framework the same engine serves.",
        units: ["U18"],
        closure: "machine",
    },
    {
        id: "F2",
        summary: "Close the two trust holes: out-of-band verifier and key fingerprints, and external chain anchoring.",
        units: ["U5", "U6", "U7"],
        closure: "machine",
    },
    {
        id: "F3",
        summary: "Expose the derivation the engine already has as the onboarding moment.",
        units: ["U1", "U17"],
        closure: "machine",
    },
    {
        id: "F4",
        summary: "One real integration: a served API, an SDK that executes, and a webhook on verdict.",
        units: ["U2", "U3", "U4", "U16"],
        closure: "machine",
    },
    {
        id: "F5",
        summary: "Dogfood in public: run Excellent's own ship/verify loop through the product and publish the pack.",
        units: ["U12"],
        // Publishing is an act, not a code path. A gate can prove the pack is
        // producible and verifiable; it cannot prove anyone published it.
        closure: "human",
    },
    {
        id: "F6",
        summary: "Split the built-vs-planned honesty into two artifacts: full candour for design partners, scoped claims for procurement.",
        units: ["U14"],
        closure: "machine",
    },
];
/* ── Gates named but not yet born ────────────────────────────────────────── */
/**
 * Gates a criterion depends on that are **not yet registered in CHECKS**.
 *
 * This list exists so the incomplete state is visible rather than either
 * (a) hidden by removing the gate name from its criterion, which would make the
 * criterion read as protected by less than it needs, or (b) papered over by
 * letting the registry assertion pass on a missing gate.
 *
 * Three properties make it safe rather than an allowlist:
 *
 *   1. It is **capped**. `PENDING_GATE_CEILING` is asserted by the gate and must
 *      only ever be lowered. A unit that lands its gate removes the entry and
 *      lowers the ceiling in the same commit.
 *   2. A pending gate is reported as `not-run`, never `pass`. So any criterion
 *      with a pending gate evaluates to `unknown` and **can never be `met`** —
 *      enforced in `evaluateBuyableCriteria` and asserted directly.
 *   3. Each entry names the unit that owes it, so it is an owned debt rather
 *      than an anonymous exemption.
 *
 * If this list is ever growing, the run is going backwards.
 */
export const PENDING_GATES = {
// EMPTY. Every gate named by every criterion is now registered in CHECKS.
// If an entry ever returns here it is a debt, not a state — see the ceiling.
};
/**
 * Shrink-only. Lower it when a unit lands its gate; never raise it.
 *
 * History, so a raise is conspicuous: 7 at U15's birth → 5 (U4 webhooks, U13
 * posture) → 3 (U8 clustering, U9 bulk) -> 2 (U10 drafts) -> 0 (U3 api-executor, U16 api-routes). At zero, every
 * criterion is protected by a gate that actually exists.
 */
export const PENDING_GATE_CEILING = 0;
export function evaluateBuyableCriteria(input) {
    const demonstrated = new Set(input.demonstrated ?? []);
    return BUYABLE_CRITERIA.map((c) => {
        const failing = [];
        const notRun = [];
        for (const gate of c.gates) {
            // A gate that does not exist yet cannot pass, whatever a caller reports.
            // Trusting `gateResults` here would let a stale or optimistic result table
            // mark a criterion met on the strength of a gate nobody has written.
            const outcome = gate in PENDING_GATES ? "not-run" : (input.gateResults[gate] ?? "not-run");
            if (outcome === "fail")
                failing.push(gate);
            else if (outcome === "not-run")
                notRun.push(gate);
        }
        const demonstrationRecorded = demonstrated.has(c.id);
        let status;
        if (failing.length > 0)
            status = "unmet";
        else if (notRun.length > 0)
            status = "unknown";
        else
            status = demonstrationRecorded ? "met" : "gates-green";
        return {
            id: c.id,
            status,
            failingGates: failing,
            notRunGates: notRun,
            demonstrationRecorded,
            caveats: c.caveats,
        };
    });
}
/** Every distinct gate name any criterion depends on. */
export function allBuyableGateNames() {
    const set = new Set();
    for (const c of BUYABLE_CRITERIA)
        for (const g of c.gates)
            set.add(g);
    return [...set].sort();
}
/**
 * Whether the product may be described as buyable.
 *
 * Deliberately strict: every criterion must be `met`, which means every gate
 * green *and* the human demonstration recorded. `gates-green` across the board
 * is explicitly not buyable, and the function says which criteria are short.
 */
export function isBuyable(evaluations) {
    const short = evaluations.filter((e) => e.status !== "met").map((e) => e.id);
    return { buyable: short.length === 0, short };
}
